import React, { useCallback, useContext, useEffect, useState } from 'react';
import { IntlProvider, useIntl } from 'react-intl';
import LangEnum from '../../lang/LangEnum';
import { Option } from '../../lib/OptionsLib';
import { Locales, loadMessages } from './messages';
import {
  TranslateType,
  TranslationContextProps,
  TranslationContextProviderProps,
} from './translationContext.types';

export const TranslationContext = React.createContext<TranslationContextProps>({
  isMessagesLoaded: false,
  changeLanguage: () => undefined,
});

export const getDefaultLocale = () => {
  const availableLocales = [
    Locales.ENGLISH,
    Locales.ENGLISH_US,
    Locales.PORTUGUESE,
    Locales.PORTUGUESE_BR,
    Locales.SPANISH,
  ];
  const findLocale = availableLocales.find((locale) => navigator.language.startsWith(locale));

  if (findLocale) {
    return findLocale;
  }

  return Locales.ENGLISH;
};

/**
 * @deprecated use isPortuguese from TranslationContext instead
 */
export const isPortuguese = (): boolean => {
  const locale = getDefaultLocale();
  return locale.startsWith(Locales.PORTUGUESE);
};

export const TranslationContextProvider = ({
  children,
  ...rest
}: TranslationContextProviderProps) => {
  const [locale, setLocale] = useState<Locales>(getDefaultLocale());
  const [messages, setMessage] = useState<any>(null);

  useEffect(() => {
    const load = async () => {
      const loadedMessages = await loadMessages(locale);
      setMessage(loadedMessages);
    };
    load();
  }, [locale]);

  return (
    <TranslationContext.Provider
      value={{ changeLanguage: setLocale, isMessagesLoaded: !!messages }}
    >
      <IntlProvider
        {...rest}
        messages={locale && messages}
        locale={locale}
        defaultLocale={Locales.PORTUGUESE}
        onError={(err) => {
          if (err.code === 'MISSING_TRANSLATION') {
            if (
              typeof err.message === 'string' &&
              err.message.startsWith('[@formatjs/intl Error MISSING_TRANSLATION] Missing message:')
            ) {
              return;
            }
          }
          if (err.code === 'FORMAT_ERROR') {
            return;
          }
          throw err;
        }}
      >
        {children}
      </IntlProvider>
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => {
  const intl = useIntl();

  const translate: TranslateType = useCallback(
    (id: LangEnum | (string & {}), values?: Record<string, any>, defaultMessage?: string) => {
      if (!id) {
        console.error('translation id not defined');
        return defaultMessage ?? 'none';
      }

      return intl.formatMessage({ id, defaultMessage: defaultMessage || id }, values);
    },
    [intl],
  );

  const translateOptions = useCallback(
    <T extends string>(options: Option[] | Readonly<Option[]>, values?: Record<string, any>) =>
      options.map((option) => ({
        ...option,
        label: translate(option.label as LangEnum, values),
        value: option.value as T,
      })),
    [translate],
  );

  return {
    ...useContext<TranslationContextProps>(TranslationContext),
    ...intl,
    locale: intl.locale as Locales,
    translate,
    isPortuguese: intl.locale === Locales.PORTUGUESE || intl.locale === Locales.PORTUGUESE_BR,
    translateOptions,
  };
};
